import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { Row } from 'components/UI/Row';
import { countries } from './countries';
import { saveSite, getSitesLoaders, getSitesApiErrors, Site, getSitesTimezones } from './sites';

type SiteFormProps = {
  code: string;
  country_code: string;
  name: string;
  time_zone: string;
};

const SiteFormSchema = object().shape({
  code: string()
    .required('sites.code is required')
    .min(3, 'sites.code is too short')
    .max(16, 'sites.code is too long'),
  country_code: string().required('sites.country is required'),
  name: string().required('sites.name is required'),
  time_zone: string()
    .nullable()
    .required('sites.timezone is required')
});

export const SiteForm: FC<{ closeFn: () => void; isOpen: boolean; site?: Site }> = ({ isOpen, closeFn, site }) => {
  const { t } = useTranslation(['translation', 'timezones']);
  const { handleSubmit, errors, control, setValue } = useForm<SiteFormProps>({
    mode: 'onBlur',
    resolver: yupResolver(SiteFormSchema)
  });
  const dispatch = useDispatch();
  const { isSaving } = useSelector(getSitesLoaders);
  const { savingError } = useSelector(getSitesApiErrors);
  const timezones = useSelector(getSitesTimezones);
  const [waitForResponse, setWaitForResponse] = useState<boolean>(false);

  // TODO: make a custom hook
  useEffect(() => {
    if (!waitForResponse && !savingError) {
      closeFn();
    }
  }, [waitForResponse, savingError]);

  useEffect(() => {
    if (site) {
      setTimeout(() => {
        const { name, code, country_code, time_zone } = site;
        setValue('name', name);
        setValue('code', code);
        setValue('country_code', country_code);
        setValue('time_zone', time_zone);
      });
    }
  }, [setValue, site]);

  const onSubmit = async (formData: SiteFormProps) => {
    setWaitForResponse(true);
    await dispatch(saveSite({ site: formData, id: site?.id }));
    setWaitForResponse(false);
  };

  return (
    <Dialog maxWidth="xs" open={isOpen} onClose={closeFn} aria-labelledby="site-form-title">
      <DialogTitle id="site-form-title">{site?.id ? t('sites.edit site') : t('sites.new site')}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Controller
            as={
              <TextField
                autoComplete="new-password"
                helperText={t(get(errors, 'name.message'))}
                error={!isEmpty(get(errors, `name`))}
                label={t('label.name')}
                style={{ marginBottom: '16px', width: '400px' }}
                variant="outlined"
              />
            }
            control={control}
            defaultValue=""
            name="name"
          />

          <Controller
            as={
              <TextField
                autoComplete="new-password"
                helperText={t(get(errors, 'code.message'))}
                error={!isEmpty(get(errors, `code`))}
                label={t('sites.unique code')}
                style={{ marginBottom: '16px', width: '400px' }}
                variant="outlined"
              />
            }
            control={control}
            defaultValue=""
            name="code"
          />

          <FormControl variant="outlined" fullWidth error={!isEmpty(get(errors, `country_code`))}>
            <InputLabel id="country-label">{t('sites.country')}</InputLabel>
            <Controller
              as={
                <Select
                  value=""
                  labelId="country-label"
                  input={<OutlinedInput label={t('sites.country')} />}
                  style={{ width: '400px' }}
                >
                  {countries.map(({ title, value }) => (
                    <MenuItem key={value} value={value}>
                      {t(`country.${value}`)}
                    </MenuItem>
                  ))}
                </Select>
              }
              control={control}
              defaultValue=""
              name="country_code"
            />
            <FormHelperText>{t(get(errors, 'country_code.message'))}</FormHelperText>
            <div style={{ height: '16px' }} />
          </FormControl>
          <FormControl variant="outlined" fullWidth error={!isEmpty(get(errors, `time_zone`))}>
            <InputLabel id="country-label">{t('sites.timezone')}</InputLabel>
            <Controller
              as={
                <Select
                  value=""
                  labelId="country-label"
                  input={<OutlinedInput label={t('sites.timezone')} />}
                  style={{ width: '400px' }}
                >
                  {timezones.map(tz => (
                    <MenuItem key={tz} value={tz}>
                      {t(tz, { ns: 'timezones' })}
                    </MenuItem>
                  ))}
                </Select>
              }
              control={control}
              defaultValue=""
              name="time_zone"
            />
            <FormHelperText>{t(get(errors, 'time_zone.message'))}</FormHelperText>
          </FormControl>
          <Row margin="20px 0" justifyContent="space-between">
            <Button color="secondary" onClick={closeFn}>
              {t('label.cancel')}
            </Button>
            <Button type="submit" color="primary" disabled={isSaving}>
              {t('label.save')}
              {isSaving && (
                <CircularProgress
                  color="primary"
                  size={34}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: -17,
                    marginLeft: -17
                  }}
                />
              )}
            </Button>
          </Row>
        </form>
      </DialogContent>
    </Dialog>
  );
};
